import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload, AlertTriangle, FileText, Plus, X, Sparkles, MapPinOff, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAbortRef, useIsMountedRef, isAbortError } from "@/hooks/use-cancellable-mutation";
import { PdfPreview } from "@/components/pdf-preview";
import type { Vendor, RenovationItem, Property } from "@shared/schema";

export type UploadMode = "invoice" | "expense" | "bid";

const EXPENSE_CATEGORIES = [
  { label: "Mortgage", value: "mortgage" },
  { label: "Property Tax", value: "property_tax" },
  { label: "Insurance", value: "insurance" },
  { label: "HOA", value: "hoa" },
  { label: "Utilities - Electric", value: "utilities_electric" },
  { label: "Utilities - Gas", value: "utilities_gas" },
  { label: "Utilities - Water", value: "utilities_water" },
  { label: "Utilities - Sewer", value: "utilities_sewer" },
  { label: "Utilities - Trash", value: "utilities_trash" },
  { label: "Utilities - Internet", value: "utilities_internet" },
  { label: "CapEx", value: "capex" },
  { label: "Repairs & Maintenance", value: "repairs_maintenance" },
  { label: "Cleaning", value: "cleaning" },
  { label: "Lawn Care / Landscaping", value: "lawn_care" },
  { label: "Pest Control", value: "pest_control" },
  { label: "Property Management", value: "property_management" },
  { label: "Furnishings", value: "furnishings" },
  { label: "Supplies", value: "supplies" },
  { label: "Other", value: "other" },
];

interface ExtractionResponse {
  documentId: number;
  fileName: string;
  mimeType: string;
  mode: UploadMode;
  extracted: any;
  confidence: number;
  warnings: string[];
  vendorMatches: Array<{ id: number; name: string; phone: string | null; email: string | null }>;
  invoiceNumberDuplicates: Array<{ id: number; fileName: string; uploadedAt: string }>;
  fileHashDuplicate: { id: number; fileName: string; uploadedAt: string } | null;
  propertyAddressMismatch?: { extractedAddress: string; currentAddress: string } | null;
}

const LOW_CONFIDENCE_THRESHOLD = 0.8;

interface Props {
  open: boolean;
  onClose: () => void;
  propertyId: number;
  mode: UploadMode;
  // Optional pre-selected renovation item (when launching from a specific item row)
  defaultRenovationItemId?: number | null;
  onSuccess?: () => void;
  // Optional file to auto-extract on open (used by the "Add to Renovations →
  // Upload Contractor Bid" flow which carries the already-chosen file across
  // navigation so the user doesn't have to re-select it).
  autoFile?: File | null;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export function DocumentUploadDialog({ open, onClose, propertyId, mode, defaultRenovationItemId, onSuccess, autoFile }: Props) {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [extraction, setExtraction] = useState<ExtractionResponse | null>(null);
  const [forceProceedDup, setForceProceedDup] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Editable form fields (populated after extraction)
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [vendorName, setVendorName] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [expenseCategory, setExpenseCategory] = useState("repairs_maintenance");
  const [expenseDescription, setExpenseDescription] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseNotes, setExpenseNotes] = useState("");
  const [bidAmount, setBidAmount] = useState("");
  const [bidRenovationItemId, setBidRenovationItemId] = useState<number | null>(null);
  const [overrideBudget, setOverrideBudget] = useState(false);
  const [lineItems, setLineItems] = useState<Array<{ description: string; amount: string; renovationItemId: number | null; createNewItem: boolean; itemType: string; itemName: string; overrideBudget?: boolean }>>([]);
  const [expenseLogMode, setExpenseLogMode] = useState<"combined" | "itemized">("combined");
  const [expenseDocumentType, setExpenseDocumentType] = useState<"receipt" | "contractor_invoice">("receipt");
  const [rememberExpensePref, setRememberExpensePref] = useState(false);
  const [expenseLineItems, setExpenseLineItems] = useState<Array<{ description: string; amount: string; category: string }>>([]);
  const [bidPreview, setBidPreview] = useState<{ diff: any[]; oldBudgetTotal: number; newBudgetTotal: number } | null>(null);
  // Bid mode: flips once "Link All" has run so the primary call to action can
  // move from Link All to "Preview budget changes".
  const [linkAllApplied, setLinkAllApplied] = useState(false);
  const [showQuickAddVendor, setShowQuickAddVendor] = useState(false);
  const [quickVendorName, setQuickVendorName] = useState("");
  const [quickVendorPhone, setQuickVendorPhone] = useState("");
  const [quickVendorEmail, setQuickVendorEmail] = useState("");
  const [quickVendorCategory, setQuickVendorCategory] = useState("other");

  const [effectivePropertyId, setEffectivePropertyId] = useState<number>(propertyId);
  useEffect(() => { setEffectivePropertyId(propertyId); }, [propertyId, open]);

  // Task #2367 — track the in-flight controller so a Cancel button can
  // tear down the underlying fetch (Gemini extract / confirm POST)
  // instead of just blocking dismissal behind a modal.
  const isMountedRef = useIsMountedRef();
  const abort = useAbortRef();

  const renovationItemsQuery = useQuery<RenovationItem[]>({
    queryKey: ["/api/app/properties", effectivePropertyId, "renovation-items"],
    enabled: open,
  });
  const vendorsQuery = useQuery<Vendor[]>({ queryKey: ["/api/app/operations/vendors"], enabled: open });
  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"], enabled: open });
  const moveDocMutation = useMutation({
    mutationFn: async (newPropertyId: number) => {
      if (!extraction) throw new Error("Missing extraction");
      const res = await apiRequest("POST", `/api/app/property-documents/${extraction.documentId}/move`, { newPropertyId });
      return res.json() as Promise<{ ok: boolean; newPropertyName: string }>;
    },
    onSuccess: (r, newPropertyId) => {
      setEffectivePropertyId(newPropertyId);
      // Clear the mismatch banner after a successful move
      setExtraction(prev => prev ? { ...prev, propertyAddressMismatch: null } : prev);
      toast({ title: "Moved", description: `Document re-assigned to ${r.newPropertyName}.` });
    },
    onError: (e: any) => toast({ title: "Move failed", description: parseErrorMessage(e, "Couldn't re-assign this document. Please try again."), variant: "destructive" }),
  });

  useEffect(() => {
    if (!open) {
      setFile(null); setFilePreviewUrl(null); setExtraction(null); setForceProceedDup(false);
      setVendorId(null); setVendorName(""); setInvoiceNumber(""); setInvoiceDate("");
      setExpenseCategory("repairs_maintenance"); setExpenseDescription(""); setExpenseAmount(""); setExpenseNotes("");
      setBidAmount(""); setBidRenovationItemId(defaultRenovationItemId ?? null); setOverrideBudget(false);
      setLineItems([]); setExpenseLineItems([]); setBidPreview(null); setLinkAllApplied(false);
      setExpenseLogMode("combined"); setExpenseDocumentType("receipt"); setRememberExpensePref(false);
      setShowQuickAddVendor(false);
      setQuickVendorName(""); setQuickVendorPhone(""); setQuickVendorEmail(""); setQuickVendorCategory("other");
    } else if (defaultRenovationItemId) {
      setBidRenovationItemId(defaultRenovationItemId);
    }
  }, [open, defaultRenovationItemId]);

  const uploadMutation = useMutation({
    mutationFn: async (data: { fileName: string; fileDataUrl: string }) => {
      const controller = abort.begin();
      try {
        const res = await apiRequest(
          "POST",
          `/api/app/properties/${propertyId}/documents/upload-and-extract`,
          { ...data, mode },
          { signal: controller.signal },
        );
        return (await res.json()) as ExtractionResponse;
      } finally {
        abort.clear(controller);
      }
    },
    onSuccess: (res) => {
      if (!isMountedRef.current) return;
      setExtraction(res);
      const ex = res.extracted || {};
      setVendorName(ex.vendorName || "");
      setInvoiceNumber(ex.invoiceNumber || "");
      setInvoiceDate(ex.invoiceDate || ex.date || ex.bidDate || "");
      if (mode === "expense") {
        setExpenseCategory(ex.category || "repairs_maintenance");
        setExpenseDescription(ex.description || ex.vendorName || "");
        setExpenseAmount(ex.totalAmount != null ? String(ex.totalAmount) : "");
        setExpenseDocumentType(ex.documentType === "invoice" || ex.lineItems?.length > 1 ? "contractor_invoice" : "receipt");
        if (Array.isArray(ex.lineItems) && ex.lineItems.length > 0) {
          setExpenseLineItems(ex.lineItems.map((li: any) => ({
            description: li.description || "",
            amount: li.amount != null ? String(li.amount) : "",
            category: ex.category || "repairs_maintenance",
          })));
        }
      } else if (mode === "bid" || mode === "invoice") {
        const items = Array.isArray(ex.lineItems) && ex.lineItems.length > 0
          ? ex.lineItems.map((li: any) => ({
              description: li.description || "",
              amount: li.amount != null ? String(li.amount) : "",
              renovationItemId: (li.suggestedRenovationItemId as number | null) ?? null,
              createNewItem: false,
              itemType: "other",
              itemName: li.description || "",
              overrideBudget: mode === "bid" ? true : undefined,
            }))
          : [{ description: ex.vendorName || (mode === "bid" ? "Bid total" : "Invoice line"), amount: ex.totalAmount != null ? String(ex.totalAmount) : "", renovationItemId: null, createNewItem: false, itemType: "other", itemName: "", overrideBudget: mode === "bid" }];
        setLineItems(items);
        if (mode === "bid") {
          setBidAmount(ex.totalAmount != null ? String(ex.totalAmount) : "");
        }
      }
      // Auto-match vendor: pick the single match, otherwise default to creating a new vendor
      if (res.vendorMatches.length === 1) {
        setVendorId(res.vendorMatches[0].id);
      } else if (res.vendorMatches.length === 0 && ex.vendorName) {
        setShowQuickAddVendor(true);
        setQuickVendorName(ex.vendorName);
        setQuickVendorPhone(ex.vendorPhone || "");
        setQuickVendorEmail(ex.vendorEmail || "");
      }
      // For low-confidence fields, blank them so the user must consciously fill in (don't trust < 80%)
      const fc: Record<string, number> = ex.fieldConfidence || {};
      if (mode === "expense") {
        if (typeof fc.category === "number" && fc.category < LOW_CONFIDENCE_THRESHOLD) setExpenseCategory("");
        if (typeof fc.documentType === "number" && fc.documentType < LOW_CONFIDENCE_THRESHOLD) setExpenseDocumentType("receipt");
      }
      // Apply user's saved expense preference (if any)
      if (mode === "expense") {
        fetch("/api/app/user-preferences", { credentials: "include" }).then(r => r.ok ? r.json() : null).then(p => {
          if (p?.expenseLogPreference === "upload" && Array.isArray(ex.lineItems) && ex.lineItems.length > 1) {
            setExpenseLogMode("itemized");
          }
        }).catch(() => {});
      }
    },
    onError: (e: any) => {
      if (isAbortError(e)) return;
      toast({ title: "Upload failed", description: parseErrorMessage(e, "Couldn't upload or extract this document. Please try again."), variant: "destructive" });
    },
  });

  const quickAddVendorMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/operations/vendors/quick-add", {
        name: quickVendorName, phone: quickVendorPhone || null, email: quickVendorEmail || null, category: quickVendorCategory,
      });
      return res.json() as Promise<Vendor>;
    },
    onSuccess: (v) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors"] });
      setVendorId(v.id);
      setVendorName(v.name);
      setShowQuickAddVendor(false);
      toast({ title: "Vendor added", description: `${v.name} added to your CRM.` });
    },
    onError: (e: any) => toast({ title: "Quick add failed", description: parseErrorMessage(e, "Couldn't add this vendor. Please try again."), variant: "destructive" }),
  });

  const confirmInvoiceMutation = useMutation({
    mutationFn: async () => {
      if (!extraction) throw new Error("Missing extraction");
      const controller = abort.begin();
      try {
        const res = await apiRequest("POST", `/api/app/property-documents/${extraction.documentId}/confirm-invoice`, {
          vendorId, vendorName: vendorName || null, invoiceNumber: invoiceNumber || null, invoiceDate: invoiceDate || null,
          lineItems: lineItems.map(li => ({
            description: li.description,
            amount: parseFloat(li.amount) || 0,
            renovationItemId: li.renovationItemId,
            createNewItem: li.createNewItem,
            itemType: li.itemType || "other",
            itemName: li.itemName || li.description,
          })),
        }, { signal: controller.signal });
        return res.json();
      } finally {
        abort.clear(controller);
      }
    },
    onSuccess: () => {
      if (!isMountedRef.current) return;
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", effectivePropertyId, "renovation-items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", effectivePropertyId, "documents"] });
      toast({ title: "Invoice applied", description: "Line items posted to renovations & expenses." });
      onSuccess?.(); onClose();
    },
    onError: (e: any) => {
      if (isAbortError(e)) return;
      toast({ title: "Failed to apply invoice", description: parseErrorMessage(e, "Couldn't apply this invoice. Please try again."), variant: "destructive" });
    },
  });

  const confirmExpenseMutation = useMutation({
    mutationFn: async () => {
      if (!extraction) throw new Error("Missing extraction");
      const controller = abort.begin();
      try {
        const res = await apiRequest("POST", `/api/app/property-documents/${extraction.documentId}/confirm-expense`, {
          documentType: expenseDocumentType,
          mode: expenseLogMode,
          category: expenseCategory,
          description: expenseDescription,
          amount: expenseAmount,
          date: invoiceDate,
          vendor: vendorName || null,
          vendorId: vendorId,
          notes: expenseNotes || null,
          lineItems: expenseLogMode === "itemized" ? expenseLineItems.map(li => ({ description: li.description, amount: li.amount, category: li.category })) : undefined,
          rememberPreference: rememberExpensePref,
        }, { signal: controller.signal });
        return res.json();
      } finally {
        abort.clear(controller);
      }
    },
    onSuccess: () => {
      if (!isMountedRef.current) return;
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", effectivePropertyId, "documents"] });
      toast({ title: "Expense logged", description: "Receipt saved and expense logged." });
      onSuccess?.(); onClose();
    },
    onError: (e: any) => {
      if (isAbortError(e)) return;
      toast({ title: "Failed to save expense", description: parseErrorMessage(e, "Couldn't save this expense. Please try again."), variant: "destructive" });
    },
  });

  const previewBidMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      if (!extraction) throw new Error("Missing extraction");
      const items = lineItems
        .filter(li => li.createNewItem || li.renovationItemId)
        .map(li => ({
          renovationItemId: li.renovationItemId,
          createNewItem: li.createNewItem,
          itemName: li.itemName || li.description,
          itemType: li.itemType || "other",
          bidAmount: parseFloat(li.amount) || 0,
          overrideBudget: li.overrideBudget !== false,
        }));
      const res = await apiRequest("POST", `/api/app/property-documents/${extraction.documentId}/preview-bid`, { items });
      return res.json() as Promise<{ diff: any[]; oldBudgetTotal: number; newBudgetTotal: number }>;
    },
    onSuccess: (d) => setBidPreview(d),
    onError: (e: any) => toast({ title: "Preview failed", description: parseErrorMessage(e, "Couldn't preview the bid changes. Please try again."), variant: "destructive" }),
  });

  const applyBidMutation = useMutation({
    mutationFn: async () => {
      if (!extraction) throw new Error("Missing extraction");
      const items = lineItems
        .filter(li => li.createNewItem || li.renovationItemId)
        .map(li => ({
          renovationItemId: li.renovationItemId,
          createNewItem: li.createNewItem,
          itemName: li.itemName || li.description,
          itemType: li.itemType || "other",
          bidAmount: parseFloat(li.amount) || 0,
          overrideBudget: li.overrideBudget !== false,
        }));
      if (items.length === 0) throw new Error("Link at least one bid line to a renovation item or create new");
      const controller = abort.begin();
      try {
        const res = await apiRequest("POST", `/api/app/property-documents/${extraction.documentId}/apply-bid`, {
          vendorId, vendorName: vendorName || null, items,
        }, { signal: controller.signal });
        return res.json();
      } finally {
        abort.clear(controller);
      }
    },
    onSuccess: () => {
      if (!isMountedRef.current) return;
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", effectivePropertyId, "renovation-items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", effectivePropertyId, "documents"] });
      toast({ title: "Bid applied", description: "Renovation items updated with bid." });
      onSuccess?.(); onClose();
    },
    onError: (e: any) => {
      if (isAbortError(e)) return;
      toast({ title: "Failed to apply bid", description: parseErrorMessage(e, "Couldn't apply this bid. Please try again."), variant: "destructive" });
    },
  });

  async function handleFile(f: File) {
    setFile(f);
    const dataUrl = await fileToDataUrl(f);
    setFilePreviewUrl(dataUrl);
    if (f.type === "application/pdf") {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(URL.createObjectURL(f));
    }
    uploadMutation.mutate({ fileName: f.name, fileDataUrl: dataUrl });
  }

  // Auto-extract a carried-over file once when the dialog opens (Add to
  // Renovations → Upload Contractor Bid). Consumed once per open cycle.
  const autoFileConsumedRef = useRef(false);
  useEffect(() => {
    if (open && autoFile && !autoFileConsumedRef.current && !file && !extraction) {
      autoFileConsumedRef.current = true;
      handleFile(autoFile);
    }
    if (!open) autoFileConsumedRef.current = false;
  }, [open, autoFile, file, extraction]);

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  const titleByMode: Record<UploadMode, string> = {
    invoice: "Upload & extract — Contractor Invoice",
    expense: "Upload & extract — Receipt / Expense",
    bid: "Upload & extract — Contractor Bid",
  };
  const lowConfidence = extraction && extraction.confidence < LOW_CONFIDENCE_THRESHOLD;
  const fieldConf: Record<string, number> = (extraction?.extracted?.fieldConfidence as Record<string, number>) || {};
  const isFieldUncertain = (key: string) => {
    const c = fieldConf[key];
    return typeof c === "number" && c < LOW_CONFIDENCE_THRESHOLD;
  };
  const uncertainFields = Object.entries(fieldConf).filter(([, v]) => v < LOW_CONFIDENCE_THRESHOLD).map(([k]) => k);
  const hasDuplicate = !!(extraction && (extraction.fileHashDuplicate || extraction.invoiceNumberDuplicates.length > 0));

  function addLineItem() {
    setLineItems([...lineItems, { description: "", amount: "", renovationItemId: null, createNewItem: false, itemType: "other", itemName: "" }]);
  }
  function updateLineItem(idx: number, patch: Partial<typeof lineItems[number]>) {
    setLineItems(lineItems.map((li, i) => i === idx ? { ...li, ...patch } : li));
  }
  function removeLineItem(idx: number) {
    setLineItems(lineItems.filter((_, i) => i !== idx));
  }

  // "Link All": auto-link every bid line to a matching existing renovation
  // item by name (mirrors the server fuzzy token match), or mark it to create
  // a new item when there's no match — writing the same per-line link state
  // manual linking uses. Per-line overrides are preserved afterward because
  // the user can still change any individual row.
  function linkAllBidLines() {
    const renoItems = renovationItemsQuery.data || [];
    const tokenize = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]+/g, " ").split(/\s+/).filter(t => t.length > 2);
    setLineItems(prev => prev.map(li => {
      const desc = (li.description || "").toLowerCase();
      let matchedId: number | null = null;
      if (desc) {
        const tokens = tokenize(desc);
        let best: { id: number; score: number } | null = null;
        for (const it of renoItems) {
          const name = (it.itemName || "").toLowerCase();
          if (!name) continue;
          const nameTokens = tokenize(name);
          let overlap = 0;
          for (const t of tokens) if (nameTokens.includes(t)) overlap++;
          const score = name === desc ? 1000 : (desc.includes(name) || name.includes(desc) ? 100 : overlap);
          if (score > 0 && (!best || score > best.score)) best = { id: it.id, score };
        }
        if (best && best.score >= (best.score >= 100 ? 1 : 2)) matchedId = best.id;
      }
      if (matchedId != null) {
        return { ...li, renovationItemId: matchedId, createNewItem: false };
      }
      return { ...li, renovationItemId: null, createNewItem: true, itemName: li.itemName || li.description };
    }));
    setLinkAllApplied(true);
  }

  // Bid-mode call-to-action staging: Link All is the blue, glowing primary
  // action until it has run (or every line is already linked / marked new);
  // after that "Preview budget changes" takes over as the primary button.
  const linkedCount = lineItems.filter(li => !li.createNewItem && li.renovationItemId).length;
  const newItemCount = lineItems.filter(li => li.createNewItem).length;
  const unlinkedCount = lineItems.length - linkedCount - newItemCount;
  const allLinesResolved = lineItems.length > 0 && unlinkedCount === 0;
  const linkStage: "link" | "preview" = (linkAllApplied || allLinesResolved) ? "preview" : "link";

  const isPdf = file?.type === "application/pdf";

  // Task #2360: gate dialog dismissal on in-flight async work so a stray
  // backdrop click / Escape / outside-pointer never silently drops the
  // result of a multi-second Gemini extract, vendor quick-add, bid
  // preview, or final confirm/apply mutation.
  const isInFlight =
    uploadMutation.isPending ||
    confirmInvoiceMutation.isPending ||
    confirmExpenseMutation.isPending ||
    previewBidMutation.isPending ||
    applyBidMutation.isPending ||
    quickAddVendorMutation.isPending ||
    moveDocMutation.isPending;

  // Task #2367: explicit cancel — abort the underlying fetch and close
  // the dialog cleanly. Without this the user could only wait for the
  // multi-second Gemini extract / confirm POST to finish.
  function cancelInFlight() {
    abort.cancel();
    onClose();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && isInFlight) return;
        if (!o) onClose();
      }}
    >
      <DialogContent
        className="max-w-6xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-document-upload"
        onInteractOutside={(e) => {
          if (isInFlight) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isInFlight) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isInFlight) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{titleByMode[mode]}</DialogTitle>
        </DialogHeader>

        {!extraction ? (
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <div className="relative inline-block mb-3">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <Sparkles className="absolute -top-1 -right-2 h-5 w-5 text-brand-accent" data-testid="icon-ai-sparkles" />
              </div>
              <p className="text-sm font-medium text-gray-700 mb-1 flex items-center justify-center gap-1.5">
                Upload &amp; extract with AI
                <Sparkles className="h-3.5 w-3.5 text-brand-accent" />
              </p>
              <p className="text-xs text-gray-500 mb-3">We'll OCR your {mode === "bid" ? "bid" : mode === "invoice" ? "invoice" : "receipt"} and pre-fill the form for you to review.</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                data-testid="input-document-file"
              />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploadMutation.isPending} data-testid="button-choose-file">
                {uploadMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Extracting…</> : "Choose file"}
              </Button>
              {uploadMutation.isPending && (
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={cancelInFlight}
                    data-testid="button-cancel-extract"
                  >
                    Cancel
                  </Button>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-2">PDF, JPG, PNG, HEIC up to 15MB</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {/* Left: file preview */}
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 min-h-[400px] flex items-center justify-center">
              {filePreviewUrl ? (
                isPdf ? (
                  pdfBlobUrl ? (
                    <div className="w-full h-[600px]" data-testid="preview-document">
                      <PdfPreview
                        // Hand react-pdf the File itself (read via ArrayBuffer,
                        // no network fetch) so the preview never depends on the
                        // page CSP allowing blob: connections. The blob URL is
                        // only used for the "Open in new tab" fallback.
                        file={file as File}
                        openInNewTabHref={pdfBlobUrl}
                        testIdPrefix="pdf-upload-preview"
                      />
                    </div>
                  ) : (
                    <FileText className="h-12 w-12 text-gray-300" />
                  )
                ) : (
                  <img src={filePreviewUrl} alt="Preview" className="max-w-full max-h-[600px] object-contain" data-testid="preview-document" />
                )
              ) : (
                <FileText className="h-12 w-12 text-gray-300" />
              )}
            </div>

            {/* Right: editable fields */}
            <div className="space-y-3">
              {extraction.propertyAddressMismatch && (
                <div className="p-3 rounded border border-red-300 bg-red-50 text-sm" data-testid="alert-address-mismatch">
                  <div className="flex items-start gap-2">
                    <MapPinOff className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                    <div className="text-red-900 flex-1">
                      <div className="font-medium">Property address doesn't match.</div>
                      <div className="text-xs mt-1">Document address: <span className="font-mono">{extraction.propertyAddressMismatch.extractedAddress}</span></div>
                      <div className="text-xs">This property: <span className="font-mono">{extraction.propertyAddressMismatch.currentAddress}</span></div>
                      <div className="text-xs mt-2 mb-1">Re-assign this document to the correct property:</div>
                      <Select
                        value={String(effectivePropertyId)}
                        onValueChange={(v) => {
                          const newId = parseInt(v);
                          if (!isNaN(newId) && newId !== effectivePropertyId) moveDocMutation.mutate(newId);
                        }}
                        disabled={moveDocMutation.isPending || !propertiesQuery.data}
                      >
                        <SelectTrigger className="bg-white text-gray-900 mt-1" data-testid="select-move-property">
                          <SelectValue placeholder={moveDocMutation.isPending ? "Moving…" : "Select a property"} />
                        </SelectTrigger>
                        <SelectContent>
                          {(propertiesQuery.data || []).map(p => (
                            <SelectItem key={p.id} value={String(p.id)}>{p.name || p.address || `Property #${p.id}`}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="text-[11px] text-red-700 mt-1.5">If the right property doesn't exist yet, save it here for now and edit it later — or close this dialog and create the new property first.</div>
                    </div>
                  </div>
                </div>
              )}

              {(lowConfidence || extraction.warnings.length > 0 || uncertainFields.length > 0) && (
                <div className="p-3 rounded border border-yellow-300 bg-yellow-50 text-sm flex items-start gap-2" data-testid="alert-low-confidence">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
                  <div className="text-yellow-900">
                    <div className="font-medium">Please double-check the extracted fields.</div>
                    {uncertainFields.length > 0 && (
                      <div className="text-xs mt-1">
                        We left these fields blank or marked them because we weren't at least 80% sure: <span className="font-medium">{uncertainFields.join(", ")}</span>.
                      </div>
                    )}
                    {extraction.warnings.map((w, i) => <div key={i} className="text-xs mt-1">{w}</div>)}
                    <div className="text-xs mt-1">Overall confidence: {Math.round(extraction.confidence * 100)}% (threshold: {Math.round(LOW_CONFIDENCE_THRESHOLD * 100)}%)</div>
                  </div>
                </div>
              )}

              {hasDuplicate && !forceProceedDup && (
                <div className="p-3 rounded border border-orange-300 bg-orange-50 text-sm" data-testid="alert-duplicate">
                  <div className="font-medium text-orange-900 mb-1 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />Possible duplicate detected</div>
                  {extraction.fileHashDuplicate && (
                    <div className="text-xs text-orange-800">An identical file was already uploaded ({extraction.fileHashDuplicate.fileName}).</div>
                  )}
                  {extraction.invoiceNumberDuplicates.map(d => (
                    <div key={d.id} className="text-xs text-orange-800">Invoice number matches a prior upload: {d.fileName}</div>
                  ))}
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => setForceProceedDup(true)} data-testid="button-proceed-duplicate">Proceed anyway</Button>
                </div>
              )}

              {/* Vendor section */}
              <div>
                <Label>Vendor</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={vendorId ? String(vendorId) : "none"} onValueChange={(v) => {
                    if (v === "__quickadd__") { setShowQuickAddVendor(true); return; }
                    if (v === "none") { setVendorId(null); return; }
                    const id = parseInt(v);
                    setVendorId(id);
                    const vend = (vendorsQuery.data || []).find(x => x.id === id);
                    if (vend) setVendorName(vend.name);
                  }}>
                    <SelectTrigger className="flex-1" data-testid="select-vendor">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {(extraction.vendorMatches.length > 0) && (
                        <>
                          {extraction.vendorMatches.map(v => (
                            <SelectItem key={`m-${v.id}`} value={String(v.id)}>{v.name} (match)</SelectItem>
                          ))}
                        </>
                      )}
                      {(vendorsQuery.data || []).filter(v => !extraction.vendorMatches.some(m => m.id === v.id)).map(v => (
                        <SelectItem key={v.id} value={String(v.id)}>{v.name}</SelectItem>
                      ))}
                      <SelectItem value="__quickadd__">+ Add new vendor…</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input className="mt-2" placeholder="Vendor name (extracted)" value={vendorName} onChange={(e) => setVendorName(e.target.value)} data-testid="input-vendor-name" />
                {showQuickAddVendor && (
                  <div className="border border-gray-200 rounded-lg p-3 mt-2 space-y-2 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold">Quick add vendor</Label>
                      <button onClick={() => setShowQuickAddVendor(false)}><X className="h-4 w-4 text-gray-400" /></button>
                    </div>
                    <Input placeholder="Name" value={quickVendorName} onChange={(e) => setQuickVendorName(e.target.value)} data-testid="input-quick-vendor-name" />
                    <Input placeholder="Phone" value={quickVendorPhone} onChange={(e) => setQuickVendorPhone(e.target.value)} data-testid="input-quick-vendor-phone" />
                    <Input placeholder="Email" value={quickVendorEmail} onChange={(e) => setQuickVendorEmail(e.target.value)} data-testid="input-quick-vendor-email" />
                    <Select value={quickVendorCategory} onValueChange={setQuickVendorCategory}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["plumber","electrician","hvac","handyman","cleaner","landscaper","pest_control","painter","other"].map(c => (
                          <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={() => quickAddVendorMutation.mutate()} disabled={!quickVendorName || quickAddVendorMutation.isPending} data-testid="button-quick-add-vendor-save">
                      {quickAddVendorMutation.isPending ? "Saving…" : "Save vendor"}
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>{mode === "bid" ? "Bid date" : mode === "expense" ? "Date" : "Invoice date"}</Label>
                  <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} data-testid="input-doc-date" />
                </div>
                {mode === "invoice" && (
                  <div>
                    <Label>Invoice #</Label>
                    <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} data-testid="input-invoice-number" />
                  </div>
                )}
              </div>

              {mode === "expense" && (
                <>
                  <div>
                    <Label>Document type</Label>
                    <Select value={expenseDocumentType} onValueChange={(v) => setExpenseDocumentType(v as any)}>
                      <SelectTrigger data-testid="select-expense-doc-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="receipt">Receipt</SelectItem>
                        <SelectItem value="contractor_invoice">Contractor Invoice</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5">
                      Category
                      {isFieldUncertain("category") && <Badge variant="outline" className="text-[10px] py-0 h-4 border-yellow-400 text-yellow-700 bg-yellow-50">AI unsure</Badge>}
                    </Label>
                    <Select value={expenseCategory} onValueChange={setExpenseCategory}>
                      <SelectTrigger data-testid="select-expense-category" className={isFieldUncertain("category") ? "border-yellow-400 ring-1 ring-yellow-200" : ""}>
                        <SelectValue placeholder={isFieldUncertain("category") ? "Pick a category — AI wasn't sure" : "Select category"} />
                      </SelectTrigger>
                      <SelectContent>
                        {EXPENSE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {expenseLineItems.length > 1 && (
                    <div className="border border-gray-200 rounded p-3 bg-blue-50">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Logging mode</Label>
                        <div className="flex gap-1">
                          <Button size="sm" type="button" variant={expenseLogMode === "combined" ? "default" : "outline"} onClick={() => setExpenseLogMode("combined")} data-testid="button-log-combined">Combined</Button>
                          <Button size="sm" type="button" variant={expenseLogMode === "itemized" ? "default" : "outline"} onClick={() => setExpenseLogMode("itemized")} data-testid="button-log-itemized">Itemized ({expenseLineItems.length})</Button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Checkbox id="remember-expense-pref" checked={rememberExpensePref} onCheckedChange={(c) => setRememberExpensePref(c === true)} data-testid="checkbox-remember-expense-pref" />
                        <Label htmlFor="remember-expense-pref" className="text-xs font-normal">Remember this choice for future uploads</Label>
                      </div>
                    </div>
                  )}
                  {expenseLogMode === "combined" || expenseLineItems.length <= 1 ? (
                    <>
                      <div>
                        <Label>Description</Label>
                        <Input value={expenseDescription} onChange={(e) => setExpenseDescription(e.target.value)} data-testid="input-expense-description" />
                      </div>
                      <div>
                        <Label>Amount</Label>
                        <Input type="number" step="0.01" value={expenseAmount} onChange={(e) => setExpenseAmount(e.target.value)} data-testid="input-expense-amount" />
                      </div>
                    </>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between">
                        <Label>Itemized line expenses</Label>
                        <Button size="sm" variant="outline" onClick={() => setExpenseLineItems([...expenseLineItems, { description: "", amount: "", category: expenseCategory }])} data-testid="button-add-expense-line"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
                      </div>
                      <div className="space-y-2 mt-2 max-h-[260px] overflow-y-auto">
                        {expenseLineItems.map((li, idx) => (
                          <div key={idx} className="border border-gray-200 rounded p-2 space-y-2" data-testid={`expense-line-${idx}`}>
                            <div className="flex gap-2">
                              <Input placeholder="Description" value={li.description} onChange={(e) => setExpenseLineItems(expenseLineItems.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} data-testid={`input-expense-line-desc-${idx}`} />
                              <Input className="w-28" type="number" step="0.01" placeholder="Amount" value={li.amount} onChange={(e) => setExpenseLineItems(expenseLineItems.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))} data-testid={`input-expense-line-amount-${idx}`} />
                              <button onClick={() => setExpenseLineItems(expenseLineItems.filter((_, i) => i !== idx))} className="text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                            <Select value={li.category} onValueChange={(v) => setExpenseLineItems(expenseLineItems.map((x, i) => i === idx ? { ...x, category: v } : x))}>
                              <SelectTrigger className="text-xs h-8" data-testid={`select-expense-line-category-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {EXPENSE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <Label>Notes</Label>
                    <Textarea value={expenseNotes} onChange={(e) => setExpenseNotes(e.target.value)} rows={2} data-testid="input-expense-notes" />
                  </div>
                </>
              )}

              {mode === "bid" && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Bid line items</Label>
                    <Button size="sm" variant="outline" onClick={addLineItem} data-testid="button-add-bid-line"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
                  </div>
                  {/* Link All call to action — kept in its own callout so the
                      button visibly belongs to its explanation. */}
                  <div
                    className={`mt-2 rounded-lg border p-3 flex items-start justify-between gap-3 ${linkStage === "link" ? "border-brand/30 bg-brand-subtle" : "border-gray-200 bg-gray-50"}`}
                    data-testid="bid-link-all-callout"
                    data-stage={linkStage}
                  >
                    {linkAllApplied ? (
                      <p className="text-xs text-gray-700" data-testid="text-link-all-summary">
                        <span className="font-medium text-green-700">{linkedCount} linked to existing</span>
                        {" · "}
                        <span className="font-medium text-brand">{newItemCount} will be created</span>
                        {unlinkedCount > 0 && <>{" · "}<span className="font-medium text-gray-500">{unlinkedCount} not linked</span></>}
                        <span className="block text-gray-500 mt-0.5">Adjust any line below, then preview the budget changes.</span>
                      </p>
                    ) : (
                      <p className="text-xs text-gray-700">
                        <span className="font-medium">Link All</span> auto-matches every line to an existing renovation item by name, or marks it to be created as a new item. Linked lines overwrite that item's budget by default.
                      </p>
                    )}
                    <Button
                      size="sm"
                      variant={linkStage === "link" ? "default" : "outline"}
                      className={`shrink-0 ${linkStage === "link" ? "animate-glow-pulse motion-reduce:animate-none" : ""}`}
                      onClick={linkAllBidLines}
                      disabled={lineItems.length === 0}
                      data-testid="button-link-all-bid"
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1" />{linkAllApplied ? "Re-run Link All" : "Link All"}
                    </Button>
                  </div>
                  <div className="space-y-2 mt-2 max-h-[300px] overflow-y-auto">
                    {lineItems.map((li, idx) => {
                      const isLinked = !li.createNewItem && !!li.renovationItemId;
                      const cardBorder = isLinked ? "border-green-300" : li.createNewItem ? "border-brand/40" : "border-gray-200";
                      return (
                      <div key={idx} className={`border ${cardBorder} rounded p-2 space-y-2`} data-testid={`bid-line-item-${idx}`}>
                        <div className="flex gap-2">
                          <Input placeholder="Description" value={li.description} onChange={(e) => updateLineItem(idx, { description: e.target.value })} data-testid={`input-bid-line-desc-${idx}`} />
                          <Input className="w-28" type="number" step="0.01" placeholder="Bid amount" value={li.amount} onChange={(e) => updateLineItem(idx, { amount: e.target.value })} data-testid={`input-bid-line-amount-${idx}`} />
                          <button onClick={() => removeLineItem(idx)} className="text-gray-400 hover:text-red-500" aria-label="Remove line item"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="flex items-start gap-2 text-xs">
                          {isLinked ? (
                            <Badge variant="outline" className="shrink-0 mt-1.5 text-[10px] py-0 h-5 border-green-300 text-green-700 bg-green-50" data-testid={`bid-line-status-linked-${idx}`}>Linked to existing</Badge>
                          ) : li.createNewItem ? (
                            <Badge variant="outline" className="shrink-0 mt-1.5 text-[10px] py-0 h-5 border-brand/40 text-brand bg-brand-subtle" data-testid={`bid-line-status-new-${idx}`}>New item</Badge>
                          ) : linkAllApplied ? (
                            <Badge variant="outline" className="shrink-0 mt-1.5 text-[10px] py-0 h-5 border-gray-300 text-gray-500 bg-gray-50" data-testid={`bid-line-status-none-${idx}`}>Not linked</Badge>
                          ) : null}
                          {!li.createNewItem ? (
                            <Select value={li.renovationItemId ? String(li.renovationItemId) : "none"} onValueChange={(v) => {
                              if (v === "__new__") { updateLineItem(idx, { createNewItem: true, renovationItemId: null }); return; }
                              if (v === "none") { updateLineItem(idx, { renovationItemId: null }); return; }
                              updateLineItem(idx, { renovationItemId: parseInt(v) });
                            }}>
                              <SelectTrigger className="text-xs h-8" data-testid={`select-bid-line-reno-${idx}`}><SelectValue placeholder="Link to renovation item" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— No link —</SelectItem>
                                {(renovationItemsQuery.data || []).map(it => (
                                  <SelectItem key={it.id} value={String(it.id)}>{it.itemName}{it.budget ? ` (budget $${it.budget})` : ""}</SelectItem>
                                ))}
                                <SelectItem value="__new__">+ Create new renovation item…</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="flex-1 space-y-1">
                              <Input className="text-xs h-8" placeholder="New item name" value={li.itemName} onChange={(e) => updateLineItem(idx, { itemName: e.target.value })} data-testid={`input-bid-line-new-name-${idx}`} />
                              <div className="flex items-center gap-1">
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => updateLineItem(idx, { createNewItem: false, itemName: "" })} data-testid={`button-bid-line-link-existing-${idx}`}>Link to existing instead</Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:text-red-700" onClick={() => removeLineItem(idx)} data-testid={`button-bid-line-remove-${idx}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Remove line item</Button>
                              </div>
                            </div>
                          )}
                        </div>
                        {isLinked && (
                          <div className="flex items-center gap-2 text-xs">
                            <Checkbox id={`override-${idx}`} checked={li.overrideBudget !== false} onCheckedChange={(c) => updateLineItem(idx, { overrideBudget: c === true })} data-testid={`checkbox-bid-override-${idx}`} />
                            <Label htmlFor={`override-${idx}`} className="text-xs font-normal">Overwrite budget with this bid amount</Label>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {mode === "invoice" && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Line items</Label>
                    <Button size="sm" variant="outline" onClick={addLineItem} data-testid="button-add-line-item"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
                  </div>
                  <div className="space-y-2 mt-2 max-h-[300px] overflow-y-auto">
                    {lineItems.map((li, idx) => (
                      <div key={idx} className="border border-gray-200 rounded p-2 space-y-2" data-testid={`line-item-${idx}`}>
                        <div className="flex gap-2">
                          <Input placeholder="Description" value={li.description} onChange={(e) => updateLineItem(idx, { description: e.target.value })} data-testid={`input-line-item-desc-${idx}`} />
                          <Input className="w-28" type="number" step="0.01" placeholder="Amount" value={li.amount} onChange={(e) => updateLineItem(idx, { amount: e.target.value })} data-testid={`input-line-item-amount-${idx}`} />
                          <button onClick={() => removeLineItem(idx)} className="text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          {!li.createNewItem ? (
                            <Select value={li.renovationItemId ? String(li.renovationItemId) : "none"} onValueChange={(v) => {
                              if (v === "__new__") { updateLineItem(idx, { createNewItem: true, renovationItemId: null }); return; }
                              if (v === "none") { updateLineItem(idx, { renovationItemId: null }); return; }
                              updateLineItem(idx, { renovationItemId: parseInt(v) });
                            }}>
                              <SelectTrigger className="text-xs h-8" data-testid={`select-line-item-reno-${idx}`}><SelectValue placeholder="Link to renovation item" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— No renovation link —</SelectItem>
                                {(renovationItemsQuery.data || []).map(it => (
                                  <SelectItem key={it.id} value={String(it.id)}>{it.itemName}</SelectItem>
                                ))}
                                <SelectItem value="__new__">+ Create new renovation item…</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="flex-1 flex gap-2">
                              <Input className="text-xs h-8" placeholder="New item name" value={li.itemName} onChange={(e) => updateLineItem(idx, { itemName: e.target.value })} data-testid={`input-line-item-new-name-${idx}`} />
                              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => updateLineItem(idx, { createNewItem: false, itemName: "" })} data-testid={`button-line-item-link-existing-${idx}`}>Link to existing instead</Button>
                              <Button size="sm" variant="ghost" className="h-8 text-xs text-red-600 hover:text-red-700" onClick={() => removeLineItem(idx)} data-testid={`button-line-item-remove-${idx}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Remove line item</Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {bidPreview && mode === "bid" && (
          <div className="border border-orange-200 bg-orange-50 rounded p-3 text-sm" data-testid="bid-preview-diff">
            <div className="font-medium text-gray-900 mb-2">Confirm budget changes</div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {bidPreview.diff.map((d: any, i: number) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-gray-700">{d.label || d.itemName}</span>
                  <span className={d.delta > 0 ? "text-orange-700" : d.delta < 0 ? "text-green-700" : "text-gray-500"}>
                    ${Number(d.oldBudget || 0).toFixed(0)} → ${Number(d.newBudget || 0).toFixed(0)}{" "}
                    {d.delta != null && <span>({d.delta > 0 ? "+" : ""}${Number(d.delta).toFixed(0)})</span>}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-between font-medium pt-2 mt-2 border-t border-orange-200 text-xs">
              <span>Total budget</span>
              <span>${bidPreview.oldBudgetTotal.toFixed(0)} → ${bidPreview.newBudgetTotal.toFixed(0)}</span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={cancelInFlight}
            data-testid="button-cancel-upload"
          >
            {isInFlight ? "Cancel" : "Close"}
          </Button>
          {extraction && mode === "bid" && !bidPreview && (
            <Button
              variant={linkStage === "preview" ? "default" : "outline"}
              disabled={previewBidMutation.isPending || (hasDuplicate && !forceProceedDup)}
              onClick={() => previewBidMutation.mutate()}
              data-testid="button-preview-bid"
            >
              {previewBidMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Calculating…</> : "Preview budget changes"}
            </Button>
          )}
          {extraction && mode === "bid" && bidPreview && (
            <Button variant="outline" onClick={() => setBidPreview(null)} data-testid="button-back-to-edit">Back to edit</Button>
          )}
          {extraction && (mode !== "bid" || bidPreview) && (
            <Button
              disabled={(hasDuplicate && !forceProceedDup) || confirmInvoiceMutation.isPending || confirmExpenseMutation.isPending || applyBidMutation.isPending}
              onClick={() => {
                if (mode === "invoice") confirmInvoiceMutation.mutate();
                else if (mode === "expense") confirmExpenseMutation.mutate();
                else if (mode === "bid") applyBidMutation.mutate();
              }}
              data-testid="button-confirm-document"
            >
              {(confirmInvoiceMutation.isPending || confirmExpenseMutation.isPending || applyBidMutation.isPending)
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
                : mode === "invoice" ? "Apply invoice" : mode === "expense" ? "Save expense" : "Confirm & apply bid"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
